import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	CheckCircleSolid,
	ExternalLinkOutline,
	Flex,
	ModalBody,
	ModalCloseButton,
	ModalFooter,
	ModalHeader,
	rem,
	Spacer,
	useToast,
} from '@bonusly/design-system';
import { EventEntity, EventFrequency } from '@bonusly/one-on-ones-entities';
import { UpdateEventAPIData, useUpdateEvent } from '@bonusly/one-on-ones-queries';
import { UserDetailEntity } from '@bonusly/user-entities';

import { useOneOnOneEvent } from '../../../../../modules/growth/components/one-on-one-meeting';
import { FormValue, QueryKey } from '../../types';
import { AnimatedBox } from '../animated-box';
import { CalendarEventDetails } from '../calendar-events-modal/calendar-events-details';
import { EventDetails } from '../event-details/event-details';
import { EventForm } from '../event-form/event-form';
import { GuestAvatar } from '../user/guest-avatar';

export interface EditMeetingContentProps {
	event: EventEntity;
	onClose: () => void;
	onDiscontinue: () => void;
	guest: UserDetailEntity;
	openLinkCalendar: VoidFunction;
}

export const EditMeetingContent: FC<EditMeetingContentProps> = ({ event, onClose, onDiscontinue, guest, openLinkCalendar }) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const { t } = useTranslation(['one_on_ones', 'common']);

	const nextMeetingDate = event.nextMeetingDate || new Date();

	const form = useForm<FormValue>({
		defaultValues: {
			dayOfWeek: event.scheduleDayOfWeek.toString(),
			frequency: event.frequency,
			occurrenceInMonth: event.scheduleOccurrenceInMonth,
			startTime: event.startTime,
			endTime: event.endTime,
			meetingDate: nextMeetingDate,
		},
	});
	const [linkedEvent, setLinkedEvent] = useState(!!event.externalCalendarLink);
	const calendarProviderTitle = t(`calendar_providers.${event.externalCalendarProvider}`);

	const {
		data: updatedEvent,
		isLoading,
		mutate,
	} = useUpdateEvent({
		onSuccess: () => {
			// These queries are not part of the global API at this time so we need
			// to manually invalidate them here
			queryClient.invalidateQueries([QueryKey.CurrentOneOnOne]);
			queryClient.invalidateQueries([QueryKey.Meetings]);
			queryClient.invalidateQueries([QueryKey.MeetingTimes, { eventId: event.id }]);
			queryClient.invalidateQueries([QueryKey.MeetingTimes, { userId: event.guestId }]);
			queryClient.invalidateQueries([QueryKey.MeetingTimes, { userId: event.hostId }]);
		},
		onError: () => {
			onClose();
			toast({ status: 'error', title: t('meeting.modal.form.error') });
		},
	});

	const submit = (data: FormValue) => {
		const payload: Partial<UpdateEventAPIData> = {};

		// Validate and set event start and end times
		if (!data.startTime) {
			form.setError('startTime', {
				type: 'custom',
				message: t('meeting.modal.form.start_time_error'),
			});
			return;
		}

		if (!data.endTime) {
			form.setError('endTime', {
				type: 'custom',
				message: t('meeting.modal.form.end_time_error'),
			});
			return;
		}

		// extract schedule components from time pickers
		const hours = data.startTime.getHours().toString().padStart(2, '0');
		const minutes = data.startTime.getMinutes().toString().padStart(2, '0');
		const seconds = data.startTime.getSeconds().toString().padStart(2, '0');
		payload.schedule_time_of_day = `${hours}:${minutes}:${seconds}`;

		// duration in seconds
		payload.schedule_duration = Math.floor((data.endTime.getTime() - data.startTime.getTime()) / 1000);

		// use browser timezone - whoever creates/edits controls the canonical timezone
		payload.schedule_time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

		// meeting date as YYYY-MM-DD
		const year = data.meetingDate.getFullYear();
		const month = (data.meetingDate.getMonth() + 1).toString().padStart(2, '0');
		const day = data.meetingDate.getDate().toString().padStart(2, '0');
		payload.meeting_date = `${year}-${month}-${day}`;

		if (data.frequency) {
			payload.frequency = data.frequency;
		}
		if (data.dayOfWeek) {
			payload.schedule_day_of_week = data.dayOfWeek;
		}

		if (data.frequency === EventFrequency.Monthly) {
			if (!data.occurrenceInMonth) {
				form.setError('occurrenceInMonth', {
					type: 'custom',
					message: t('meeting.modal.form.occurrence_in_month_error'),
				});
				return;
			}
			payload.schedule_occurrence_in_month = data.occurrenceInMonth;
		} else {
			// explicitly set to null when frequency is not monthly
			payload.schedule_occurrence_in_month = null;
		}

		if (!linkedEvent) {
			payload.external_calendar_id = null;
			payload.external_calendar_link = null;
			payload.external_calendar_provider = null;
		}

		mutate({ eventOrId: event.id, data: payload });
	};

	const minModalHeight = updatedEvent || (event.externalCalendarId && linkedEvent) ? 320 : 440;

	const { data: eventDTO } = useOneOnOneEvent(event.id);
	const eventStartTime = eventDTO?.lastMeetingStartTime || event.startTime;
	const eventEndTime = eventDTO?.lastMeetingEndTime || event.endTime;

	return (
		<AnimatedBox minH={minModalHeight}>
			<Box as="form" onSubmit={form.handleSubmit(submit)} tabIndex={0}>
				<ModalHeader borderBottom={1} borderColor="border.soft" borderStyle="solid">
					{updatedEvent ? (
						<Flex alignItems="center" gap={rem(10)}>
							<CheckCircleSolid color="points.fg" size="lg" />
							<Box>{t('meeting.modal.success_header_edit')}</Box>
						</Flex>
					) : (
						t('meeting.modal.header_edit')
					)}
				</ModalHeader>
				<ModalCloseButton size="lg" borderRadius="full" />

				<ModalBody px={5} pt={4} pb={0}>
					<Flex direction="column" gap={5}>
						<GuestAvatar guest={guest} event={updatedEvent} />

						{event.externalCalendarId && linkedEvent ? (
							<CalendarEventDetails
								startDateTime={eventStartTime}
								endDateTime={eventEndTime}
								externalCalendarLink={event.externalCalendarLink}
								externalCalendarProvider={event.externalCalendarProvider}
								frequency={event.frequency}
								occurrenceInMonth={event.scheduleOccurrenceInMonth}
								onUnlinkEvent={() => setLinkedEvent(false)}
								title={calendarProviderTitle}
								scheduleDayOfWeek={String(event.scheduleDayOfWeek)}
							/>
						) : (
							<>
								{updatedEvent ? (
									<EventDetails event={updatedEvent} onClose={onClose} />
								) : (
									<FormProvider {...form}>
										<EventForm isNewMeeting={false} openLinkCalendar={openLinkCalendar} />
									</FormProvider>
								)}
							</>
						)}
					</Flex>
				</ModalBody>

				<ModalFooter px={{ base: 3, md: 5 }} py={{ base: 3, md: 5 }} justifyContent="space-between">
					{updatedEvent ? (
						<>
							<Spacer />
							<Button colorScheme="secondary" variant="alpha" onClick={onClose}>
								{t('common:close')}
							</Button>
						</>
					) : (
						<Flex alignItems="center" gap={2} justifyContent="space-between" width="full">
							<Button colorScheme="secondary" onClick={onDiscontinue} variant="alpha">
								{t('one_on_ones:meeting.modal.discontinue.button')}
							</Button>

							<Flex gap={{ base: 1, md: 2 }}>
								<Button color="primary.fg" isDisabled={isLoading} onClick={onClose} variant="ghost">
									{t('common:cancel')}
								</Button>
								{event.externalCalendarId && event.externalCalendarLink && linkedEvent ? (
									<Button
										as="a"
										colorScheme="primary"
										href={event.externalCalendarLink}
										target="_blank"
										rel="noopener noreferrer"
										rightIcon={<ExternalLinkOutline />}
									>
										{t('one_on_ones:meeting.modal.form.manage')}
									</Button>
								) : (
									<Button colorScheme="primary" isActive={isLoading} type="submit">
										{t('one_on_ones:meeting.modal.form.submit_edit')}
									</Button>
								)}
							</Flex>
						</Flex>
					)}
				</ModalFooter>
			</Box>
		</AnimatedBox>
	);
};
